import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  ArrowLeft, 
  Camera, 
  Droplets, 
  Save,
  Loader2,
  CheckCircle2,
  Mail,
  Clock,
  Users,
  Database,
  Settings as SettingsIcon,
  Send,
  MessageCircle,
  Plus,
  Trash2,
  Info,
  AlertTriangle,
  TestTube,
  Fingerprint,
  ShieldCheck,
  Loader2 as Spinner
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { enrollFingerprint, getFingerprintStatus, removeFingerprint, isWebAuthnAvailable } from "@/lib/webauthn";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return { Authorization: `Bearer ${token}` };
};

// Email Configuration Component
const EmailSettings = () => {
  const [config, setConfig] = useState({
    recipients: [],
    schedule_hours: [7, 20],
    enabled: true
  });
  const [newEmail, setNewEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/system/email-config`, {
        headers: getAuthHeaders()
      });
      setConfig(response.data);
    } catch (error) {
      console.error("Error fetching email config:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/system/email-config`, config, {
        headers: getAuthHeaders()
      });
      toast.success("Configuración de emails guardada");
    } catch (error) {
      toast.error("Error al guardar configuración");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      await axios.post(`${API}/email/test-report`, {}, {
        headers: getAuthHeaders()
      });
      toast.success("Email de prueba enviado");
    } catch (error) {
      toast.error("Error al enviar email de prueba");
    } finally {
      setTesting(false);
    }
  };

  const addEmail = () => {
    if (newEmail && newEmail.includes("@")) {
      setConfig(prev => ({
        ...prev,
        recipients: [...prev.recipients, newEmail]
      }));
      setNewEmail("");
    }
  };

  const removeEmail = (email) => {
    setConfig(prev => ({
      ...prev,
      recipients: prev.recipients.filter(e => e !== email)
    }));
  };

  const toggleHour = (hour) => {
    setConfig(prev => {
      const hours = prev.schedule_hours || [];
      if (hours.includes(hour)) {
        return { ...prev, schedule_hours: hours.filter(h => h !== hour) };
      } else {
        return { ...prev, schedule_hours: [...hours, hour].sort((a, b) => a - b) };
      }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="bg-[#141415] border-zinc-800">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-heading text-base uppercase tracking-wider text-zinc-200 flex items-center gap-2">
                <Mail className="w-4 h-4 text-blue-400" />
                Configuración de Emails
              </CardTitle>
              <CardDescription className="text-zinc-500">
                Configura los destinatarios y horarios de los informes automáticos
              </CardDescription>
            </div>
            <Switch
              checked={config.enabled}
              onCheckedChange={(checked) => setConfig(prev => ({ ...prev, enabled: checked }))}
            />
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Recipients */}
          <div className="space-y-3">
            <Label className="text-zinc-400 text-xs uppercase tracking-wider flex items-center gap-2">
              <Users className="w-3 h-3" />
              Destinatarios
            </Label>
            <div className="flex gap-2">
              <Input
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="ejemplo@email.com"
                className="bg-zinc-900 border-zinc-700 text-zinc-100"
                onKeyDown={(e) => e.key === "Enter" && addEmail()}
              />
              <Button onClick={addEmail} variant="outline" className="border-zinc-700">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {config.recipients.map((email, idx) => (
                <Badge key={idx} variant="secondary" className="bg-zinc-800 text-zinc-300 py-1 px-2 flex items-center gap-1">
                  {email}
                  <button onClick={() => removeEmail(email)} className="ml-1 hover:text-red-400">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
              {config.recipients.length === 0 && (
                <span className="text-zinc-600 text-sm">No hay destinatarios configurados</span>
              )}
            </div>
          </div>

          {/* Schedule */}
          <div className="space-y-3">
            <Label className="text-zinc-400 text-xs uppercase tracking-wider flex items-center gap-2">
              <Clock className="w-3 h-3" />
              Horarios de Envío
            </Label>
            <div className="flex flex-wrap gap-2">
              {[0, 6, 7, 8, 12, 14, 18, 19, 20, 21, 22].map(hour => (
                <Button
                  key={hour}
                  variant={config.schedule_hours?.includes(hour) ? "default" : "outline"}
                  size="sm"
                  onClick={() => toggleHour(hour)}
                  className={config.schedule_hours?.includes(hour) 
                    ? "bg-blue-600 hover:bg-blue-700" 
                    : "border-zinc-700 text-zinc-400"
                  }
                >
                  {hour.toString().padStart(2, "0")}:00
                </Button>
              ))}
            </div>
            <p className="text-xs text-zinc-500">
              Selecciona las horas del día en las que se enviarán los informes
            </p>
          </div>

          <Separator className="bg-zinc-800" />

          <div className="flex justify-between">
            <Button
              onClick={handleTest}
              disabled={testing || !config.enabled}
              variant="outline"
              className="border-zinc-700 text-zinc-300"
            >
              {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Enviar Prueba
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Guardar
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Telegram Configuration Component
const TelegramSettings = () => {
  const [config, setConfig] = useState({
    bot_token: "",
    chat_ids: [],
    enabled: false,
    send_alerts: true,
    send_daily_reports: true
  });
  const [newChatId, setNewChatId] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [botInfo, setBotInfo] = useState(null);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/system/telegram-config`, {
        headers: getAuthHeaders()
      });
      setConfig(response.data);
      if (response.data.bot_token) {
        fetchBotInfo();
      }
    } catch (error) {
      console.error("Error fetching telegram config:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBotInfo = async () => {
    try {
      const response = await axios.get(`${API}/telegram/bot-info`, {
        headers: getAuthHeaders()
      });
      setBotInfo(response.data);
    } catch (error) {
      console.error("Error fetching bot info:", error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/system/telegram-config`, config, {
        headers: getAuthHeaders()
      });
      toast.success("Configuración de Telegram guardada");
      if (config.bot_token) {
        fetchBotInfo();
      }
    } catch (error) {
      toast.error("Error al guardar configuración");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      await axios.post(`${API}/telegram/test`, {}, {
        headers: getAuthHeaders()
      });
      toast.success("Mensaje de prueba enviado a Telegram");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar mensaje de prueba");
    } finally {
      setTesting(false);
    }
  };

  const addChatId = () => {
    if (newChatId) {
      setConfig(prev => ({
        ...prev,
        chat_ids: [...prev.chat_ids, newChatId]
      }));
      setNewChatId("");
    }
  };

  const removeChatId = (chatId) => {
    setConfig(prev => ({
      ...prev,
      chat_ids: prev.chat_ids.filter(id => id !== chatId)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="bg-[#141415] border-zinc-800">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-heading text-base uppercase tracking-wider text-zinc-200 flex items-center gap-2">
                <MessageCircle className="w-4 h-4 text-blue-400" />
                Configuración de Telegram
              </CardTitle>
              <CardDescription className="text-zinc-500">
                Recibe alertas y informes diarios por Telegram (gratuito)
              </CardDescription>
            </div>
            <Switch
              checked={config.enabled}
              onCheckedChange={(checked) => setConfig(prev => ({ ...prev, enabled: checked }))}
            />
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Bot Token */}
          <div className="space-y-3">
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">
              Token del Bot
            </Label>
            <Input
              type="password"
              value={config.bot_token}
              onChange={(e) => setConfig(prev => ({ ...prev, bot_token: e.target.value }))}
              placeholder="123456789:ABCdefGHIjklMNOpqrSTUvwxYZ"
              className="bg-zinc-900 border-zinc-700 text-zinc-100 font-mono"
            />
            <p className="text-xs text-zinc-500">
              Obtén tu token creando un bot con <a href="https://t.me/BotFather" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">@BotFather</a> en Telegram
            </p>
            {botInfo && botInfo.bot_username && (
              <Badge variant="outline" className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                <CheckCircle2 className="w-3 h-3 mr-1" />
                Bot: @{botInfo.bot_username}
              </Badge>
            )}
          </div>

          {/* Chat IDs */}
          <div className="space-y-3">
            <Label className="text-zinc-400 text-xs uppercase tracking-wider flex items-center gap-2">
              Chat IDs (grupos o usuarios)
            </Label>
            <div className="flex gap-2">
              <Input
                value={newChatId}
                onChange={(e) => setNewChatId(e.target.value)}
                placeholder="-1001234567890 o 123456789"
                className="bg-zinc-900 border-zinc-700 text-zinc-100 font-mono"
                onKeyDown={(e) => e.key === "Enter" && addChatId()}
              />
              <Button onClick={addChatId} variant="outline" className="border-zinc-700">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {config.chat_ids.map((chatId, idx) => (
                <Badge key={idx} variant="secondary" className="bg-zinc-800 text-zinc-300 py-1 px-2 flex items-center gap-1 font-mono">
                  {chatId}
                  <button onClick={() => removeChatId(chatId)} className="ml-1 hover:text-red-400">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
              {config.chat_ids.length === 0 && (
                <span className="text-zinc-600 text-sm">No hay chats configurados</span>
              )}
            </div>
            <div className="bg-blue-950/30 border border-blue-900/50 rounded-lg p-3">
              <p className="text-xs text-blue-300 flex items-start gap-2">
                <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>
                  Para obtener tu Chat ID: añade el bot a tu grupo, envía un mensaje y ve a 
                  <code className="bg-blue-900/50 px-1 mx-1 rounded">https://api.telegram.org/bot[TOKEN]/getUpdates</code>
                </span>
              </p>
            </div>
          </div>

          {/* Options */}
          <div className="space-y-3">
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">
              Opciones
            </Label>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-zinc-300">Enviar alertas de nivel</span>
                <Switch
                  checked={config.send_alerts}
                  onCheckedChange={(checked) => setConfig(prev => ({ ...prev, send_alerts: checked }))}
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-zinc-300">Enviar informes diarios</span>
                <Switch
                  checked={config.send_daily_reports}
                  onCheckedChange={(checked) => setConfig(prev => ({ ...prev, send_daily_reports: checked }))}
                />
              </div>
            </div>
          </div>

          <Separator className="bg-zinc-800" />

          <div className="flex justify-between">
            <Button
              onClick={handleTest}
              disabled={testing || !config.enabled || !config.bot_token || config.chat_ids.length === 0}
              variant="outline"
              className="border-zinc-700 text-zinc-300"
            >
              {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <TestTube className="w-4 h-4 mr-2" />}
              Enviar Prueba
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Guardar
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Dam Data Configuration Component
const DamDataSettings = () => {
  const [damData, setDamData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  useEffect(() => {
    fetchDamData();
  }, []);

  const fetchDamData = async () => {
    try {
      const response = await axios.get(`${API}/system/dam-data`, {
        headers: getAuthHeaders()
      });
      setDamData(response.data);
    } catch (error) {
      console.error("Error fetching dam data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (damName) => {
    setSaving(prev => ({ ...prev, [damName]: true }));
    try {
      await axios.put(`${API}/system/dam-data/${damName}`, damData[damName], {
        headers: getAuthHeaders()
      });
      toast.success(`Datos de ${damName} guardados`);
    } catch (error) {
      toast.error("Error al guardar datos");
    } finally {
      setSaving(prev => ({ ...prev, [damName]: false }));
    }
  };

  const handleChange = (damName, field, value) => {
    setDamData(prev => ({
      ...prev,
      [damName]: {
        ...prev[damName],
        [field]: parseFloat(value) || 0
      }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="mb-4">
        <p className="text-sm text-zinc-500">
          Estos datos se utilizan en el simulador hidrológico para calcular el aporte de lluvia a cada presa.
        </p>
      </div>
      
      {Object.entries(damData).map(([damName, data]) => (
        <Card key={damName} className="bg-[#141415] border-zinc-800">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-base uppercase tracking-wider text-zinc-200 flex items-center gap-2">
              <Droplets className="w-4 h-4 text-blue-400" />
              {damName}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">
                  Capacidad (hm³)
                </Label>
                <Input
                  type="number"
                  step="0.1"
                  value={data.capacity_hm3}
                  onChange={(e) => handleChange(damName, 'capacity_hm3', e.target.value)}
                  className="bg-zinc-900 border-zinc-700 text-zinc-100 font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">
                  Área Cuenca (km²)
                </Label>
                <Input
                  type="number"
                  step="0.1"
                  value={data.basin_area_km2}
                  onChange={(e) => handleChange(damName, 'basin_area_km2', e.target.value)}
                  className="bg-zinc-900 border-zinc-700 text-zinc-100 font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">
                  Superficie Embalse (ha)
                </Label>
                <Input
                  type="number"
                  step="0.1"
                  value={data.reservoir_surface_ha}
                  onChange={(e) => handleChange(damName, 'reservoir_surface_ha', e.target.value)}
                  className="bg-zinc-900 border-zinc-700 text-zinc-100 font-mono"
                />
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">
                  Nivel Máximo (m)
                </Label>
                <Input
                  type="number"
                  step="0.1"
                  value={data.max_level}
                  onChange={(e) => handleChange(damName, 'max_level', e.target.value)}
                  className="bg-zinc-900 border-zinc-700 text-zinc-100 font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">
                  Nivel Mínimo (m)
                </Label>
                <Input
                  type="number"
                  step="0.1"
                  value={data.min_level || 0}
                  onChange={(e) => handleChange(damName, 'min_level', e.target.value)}
                  className="bg-zinc-900 border-zinc-700 text-zinc-100 font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">
                  Coef. Escorrentía
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="1"
                  value={data.coefficient}
                  onChange={(e) => handleChange(damName, 'coefficient', e.target.value)}
                  className="bg-zinc-900 border-zinc-700 text-zinc-100 font-mono"
                />
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                onClick={() => handleSave(damName)}
                disabled={saving[damName]}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {saving[damName] ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Guardar
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

const CameraSettings = ({ cameras, onUpdateCamera }) => {
  const [editedCameras, setEditedCameras] = useState(
    cameras.reduce((acc, cam) => ({ ...acc, [cam.id]: { ...cam } }), {})
  );
  const [saving, setSaving] = useState({});

  const handleChange = (cameraId, field, value) => {
    setEditedCameras(prev => ({
      ...prev,
      [cameraId]: {
        ...prev[cameraId],
        [field]: value
      }
    }));
  };

  const handleSave = async (cameraId) => {
    setSaving(prev => ({ ...prev, [cameraId]: true }));
    try {
      const camera = editedCameras[cameraId];
      await onUpdateCamera(cameraId, {
        name: camera.name,
        url: camera.url,
        location: camera.location,
        status: camera.url ? "online" : "offline"
      });
      toast.success(`${camera.name} actualizada`);
    } catch (error) {
      toast.error("Error al guardar");
    } finally {
      setSaving(prev => ({ ...prev, [cameraId]: false }));
    }
  };

  return (
    <div className="space-y-4">
      {cameras.map((camera) => {
        const edited = editedCameras[camera.id] || camera;
        const hasChanges = 
          edited.name !== camera.name || 
          edited.url !== camera.url || 
          edited.location !== camera.location;

        return (
          <Card 
            key={camera.id} 
            className="bg-[#141415] border-zinc-800"
            data-testid={`camera-settings-${camera.id}`}
          >
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-base uppercase tracking-wider text-zinc-200">
                  {camera.name}
                </CardTitle>
                {hasChanges && (
                  <span className="text-xs text-amber-500 font-mono">Sin guardar</span>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor={`name-${camera.id}`} className="text-zinc-400 text-xs uppercase tracking-wider">
                    Nombre
                  </Label>
                  <Input
                    id={`name-${camera.id}`}
                    value={edited.name}
                    onChange={(e) => handleChange(camera.id, 'name', e.target.value)}
                    className="bg-zinc-900 border-zinc-700 text-zinc-100"
                    data-testid={`camera-name-input-${camera.id}`}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor={`location-${camera.id}`} className="text-zinc-400 text-xs uppercase tracking-wider">
                    Ubicación
                  </Label>
                  <Input
                    id={`location-${camera.id}`}
                    value={edited.location}
                    onChange={(e) => handleChange(camera.id, 'location', e.target.value)}
                    className="bg-zinc-900 border-zinc-700 text-zinc-100"
                    data-testid={`camera-location-input-${camera.id}`}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor={`url-${camera.id}`} className="text-zinc-400 text-xs uppercase tracking-wider">
                  URL de Streaming / Imagen
                </Label>
                <Input
                  id={`url-${camera.id}`}
                  value={edited.url}
                  onChange={(e) => handleChange(camera.id, 'url', e.target.value)}
                  placeholder="https://example.com/stream.jpg o rtsp://..."
                  className="bg-zinc-900 border-zinc-700 text-zinc-100 font-mono text-sm"
                  data-testid={`camera-url-input-${camera.id}`}
                />
                <p className="text-xs text-zinc-500">
                  Ingrese la URL de la imagen o stream de la cámara
                </p>
              </div>
              <div className="flex justify-end">
                <Button
                  onClick={() => handleSave(camera.id)}
                  disabled={saving[camera.id] || !hasChanges}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid={`save-camera-btn-${camera.id}`}
                >
                  {saving[camera.id] ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Guardar
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

const SensorSettings = ({ sensors, onUpdateSensor }) => {
  const [editedSensors, setEditedSensors] = useState(
    sensors.reduce((acc, sensor) => ({ ...acc, [sensor.id]: { ...sensor } }), {})
  );
  const [saving, setSaving] = useState({});

  const handleChange = (sensorId, field, value) => {
    setEditedSensors(prev => ({
      ...prev,
      [sensorId]: {
        ...prev[sensorId],
        [field]: field.includes('threshold') ? parseFloat(value) || 0 : value
      }
    }));
  };

  const handleSave = async (sensorId) => {
    setSaving(prev => ({ ...prev, [sensorId]: true }));
    try {
      const sensor = editedSensors[sensorId];
      await onUpdateSensor(sensorId, {
        name: sensor.name,
        location: sensor.location,
        min_threshold: sensor.min_threshold,
        max_threshold: sensor.max_threshold
      });
      toast.success(`${sensor.name} actualizada`);
    } catch (error) {
      toast.error("Error al guardar");
    } finally {
      setSaving(prev => ({ ...prev, [sensorId]: false }));
    }
  };

  return (
    <div className="space-y-4">
      {sensors.map((sensor) => {
        const edited = editedSensors[sensor.id] || sensor;
        const hasChanges = 
          edited.name !== sensor.name || 
          edited.location !== sensor.location ||
          edited.min_threshold !== sensor.min_threshold ||
          edited.max_threshold !== sensor.max_threshold;

        return (
          <Card 
            key={sensor.id} 
            className="bg-[#141415] border-zinc-800"
            data-testid={`sensor-settings-${sensor.id}`}
          >
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-heading text-base uppercase tracking-wider text-zinc-200">
                    {sensor.name}
                  </CardTitle>
                  <CardDescription className="font-mono text-xs">
                    Nivel actual: {sensor.current_level?.toFixed(2)} m
                  </CardDescription>
                </div>
                {hasChanges && (
                  <span className="text-xs text-amber-500 font-mono">Sin guardar</span>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor={`sensor-name-${sensor.id}`} className="text-zinc-400 text-xs uppercase tracking-wider">
                    Nombre
                  </Label>
                  <Input
                    id={`sensor-name-${sensor.id}`}
                    value={edited.name}
                    onChange={(e) => handleChange(sensor.id, 'name', e.target.value)}
                    className="bg-zinc-900 border-zinc-700 text-zinc-100"
                    data-testid={`sensor-name-input-${sensor.id}`}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor={`sensor-location-${sensor.id}`} className="text-zinc-400 text-xs uppercase tracking-wider">
                    Ubicación
                  </Label>
                  <Input
                    id={`sensor-location-${sensor.id}`}
                    value={edited.location}
                    onChange={(e) => handleChange(sensor.id, 'location', e.target.value)}
                    className="bg-zinc-900 border-zinc-700 text-zinc-100"
                    data-testid={`sensor-location-input-${sensor.id}`}
                  />
                </div>
              </div>
              <Separator className="bg-zinc-800" />
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor={`min-threshold-${sensor.id}`} className="text-zinc-400 text-xs uppercase tracking-wider">
                    Umbral Mínimo (m)
                  </Label>
                  <Input
                    id={`min-threshold-${sensor.id}`}
                    type="number"
                    step="0.1"
                    value={edited.min_threshold}
                    onChange={(e) => handleChange(sensor.id, 'min_threshold', e.target.value)}
                    className="bg-zinc-900 border-zinc-700 text-zinc-100 font-mono"
                    data-testid={`sensor-min-input-${sensor.id}`}
                  />
                  <p className="text-xs text-blue-400">Alerta de nivel bajo</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor={`max-threshold-${sensor.id}`} className="text-zinc-400 text-xs uppercase tracking-wider">
                    Umbral Máximo (m)
                  </Label>
                  <Input
                    id={`max-threshold-${sensor.id}`}
                    type="number"
                    step="0.1"
                    value={edited.max_threshold}
                    onChange={(e) => handleChange(sensor.id, 'max_threshold', e.target.value)}
                    className="bg-zinc-900 border-zinc-700 text-zinc-100 font-mono"
                    data-testid={`sensor-max-input-${sensor.id}`}
                  />
                  <p className="text-xs text-red-400">Alerta de nivel crítico</p>
                </div>
              </div>
              <div className="flex justify-end">
                <Button
                  onClick={() => handleSave(sensor.id)}
                  disabled={saving[sensor.id] || !hasChanges}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid={`save-sensor-btn-${sensor.id}`}
                >
                  {saving[sensor.id] ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Guardar
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

const SecuritySettings = () => {
  const supported = isWebAuthnAvailable();
  const [enrolled, setEnrolled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (!supported) {
      setLoading(false);
      return;
    }
    getFingerprintStatus()
      .then((s) => setEnrolled(!!s.enrolled))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [supported]);

  const handleToggle = async () => {
    setWorking(true);
    try {
      if (enrolled) {
        await removeFingerprint();
        setEnrolled(false);
        toast.success("Huella eliminada de este dispositivo");
      } else {
        await enrollFingerprint();
        setEnrolled(true);
        toast.success("Acceso por huella activado en este dispositivo");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "No se pudo configurar la huella");
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="bg-[#141415] border-zinc-800" data-testid="security-fingerprint-card">
        <CardHeader>
          <CardTitle className="font-heading text-base uppercase tracking-wider text-zinc-200 flex items-center gap-2">
            <Fingerprint className="w-4 h-4 text-emerald-400" />
            Acceso por Huella Dactilar
          </CardTitle>
          <CardDescription className="text-zinc-500">
            Activa el inicio de sesión biométrico (huella, Face ID o PIN del dispositivo) para entrar sin escribir la contraseña.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {!supported ? (
            <div className="bg-amber-950/30 border border-amber-900/50 rounded-lg p-4">
              <p className="text-sm text-amber-300 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>
                  Este navegador o dispositivo no admite acceso por huella. Requiere una conexión segura (HTTPS)
                  y un dispositivo con lector biométrico (móvil con huella/Face ID o portátil con Windows Hello/Touch ID).
                </span>
              </p>
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center p-6">
              <Spinner className="w-6 h-6 animate-spin text-zinc-500" />
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between rounded-lg border border-zinc-800 bg-zinc-900/60 p-4">
                <div className="flex items-center gap-3">
                  <div className={`flex h-10 w-10 items-center justify-center rounded-full ${enrolled ? "bg-emerald-500/15 text-emerald-400" : "bg-zinc-800 text-zinc-500"}`}>
                    <ShieldCheck className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-zinc-200">
                      {enrolled ? "Huella activada en este dispositivo" : "Huella no configurada"}
                    </p>
                    <p className="text-xs text-zinc-500">
                      {enrolled
                        ? "Podrás entrar con tu huella en el próximo inicio de sesión."
                        : "Activa la huella para iniciar sesión más rápido y seguro."}
                    </p>
                  </div>
                </div>
                <Badge
                  variant="outline"
                  className={enrolled
                    ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                    : "bg-zinc-800 text-zinc-400 border-zinc-700"}
                >
                  {enrolled ? "Activada" : "Inactiva"}
                </Badge>
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={handleToggle}
                  disabled={working}
                  className={enrolled
                    ? "bg-red-600/90 hover:bg-red-700 text-white"
                    : "bg-emerald-600 hover:bg-emerald-700 text-white"}
                  data-testid="security-fingerprint-toggle-btn"
                >
                  {working ? (
                    <Spinner className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Fingerprint className="w-4 h-4 mr-2" />
                  )}
                  {enrolled ? "Quitar huella de este dispositivo" : "Activar acceso por huella"}
                </Button>
              </div>

              <div className="bg-blue-950/30 border border-blue-900/50 rounded-lg p-3">
                <p className="text-xs text-blue-300 flex items-start gap-2">
                  <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>
                    La huella se guarda únicamente en este dispositivo. Para usarla en el móvil, inicia sesión en el móvil
                    y activa la huella desde allí. Cada usuario debe activarla en sus propios dispositivos.
                  </span>
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const Settings = ({ cameras, sensors, onUpdateCamera, onUpdateSensor, onRefresh }) => {
  // Fetch data on mount if not already loaded
  useEffect(() => {
    if (cameras.length === 0 || sensors.length === 0) {
      onRefresh();
    }
  }, [cameras.length, sensors.length, onRefresh]);

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="settings-page">
      {/* Header */}
      <header className="bg-[#0F0F10]/90 backdrop-blur-md border-b border-zinc-800 px-4 py-3 md:px-6 md:py-4 sticky top-0 z-50">
        <div className="flex items-center gap-4 max-w-4xl mx-auto">
          <Link to="/">
            <Button 
              variant="ghost" 
              size="icon"
              className="text-zinc-400 hover:text-zinc-100"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <h1 className="font-heading font-black text-lg uppercase tracking-widest text-zinc-100">
            Configuración
          </h1>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto p-4 md:p-6">
        <Tabs defaultValue="system" className="space-y-6">
          <TabsList className="bg-zinc-900 border border-zinc-800 flex-wrap h-auto gap-1 p-1">
            <TabsTrigger 
              value="system" 
              className="data-[state=active]:bg-blue-600 data-[state=active]:text-white"
              data-testid="system-tab"
            >
              <SettingsIcon className="w-4 h-4 mr-2" />
              Sistema
            </TabsTrigger>
            <TabsTrigger 
              value="notifications" 
              className="data-[state=active]:bg-blue-600 data-[state=active]:text-white"
              data-testid="notifications-tab"
            >
              <Mail className="w-4 h-4 mr-2" />
              Notificaciones
            </TabsTrigger>
            <TabsTrigger 
              value="cameras" 
              className="data-[state=active]:bg-blue-600 data-[state=active]:text-white"
              data-testid="cameras-tab"
            >
              <Camera className="w-4 h-4 mr-2" />
              Cámaras
            </TabsTrigger>
            <TabsTrigger 
              value="sensors"
              className="data-[state=active]:bg-blue-600 data-[state=active]:text-white"
              data-testid="sensors-tab"
            >
              <Droplets className="w-4 h-4 mr-2" />
              Sondas
            </TabsTrigger>
            <TabsTrigger 
              value="security"
              className="data-[state=active]:bg-blue-600 data-[state=active]:text-white"
              data-testid="security-tab"
            >
              <Fingerprint className="w-4 h-4 mr-2" />
              Seguridad
            </TabsTrigger>
          </TabsList>

          <TabsContent value="system">
            <div className="mb-4">
              <h2 className="font-heading font-bold uppercase tracking-wider text-zinc-300 flex items-center gap-2">
                <Database className="w-4 h-4 text-blue-400" />
                Datos de las Presas
              </h2>
              <p className="text-sm text-zinc-500 mt-1">
                Parámetros hidrológicos utilizados en el simulador de avenidas
              </p>
            </div>
            <DamDataSettings />
          </TabsContent>

          <TabsContent value="notifications">
            <div className="space-y-6">
              <div className="mb-4">
                <h2 className="font-heading font-bold uppercase tracking-wider text-zinc-300">
                  Configurar Notificaciones
                </h2>
                <p className="text-sm text-zinc-500 mt-1">
                  Configura email y Telegram para recibir alertas e informes
                </p>
              </div>
              <EmailSettings />
              <TelegramSettings />
            </div>
          </TabsContent>

          <TabsContent value="cameras">
            <div className="mb-4">
              <h2 className="font-heading font-bold uppercase tracking-wider text-zinc-300">
                Configurar Cámaras
              </h2>
              <p className="text-sm text-zinc-500 mt-1">
                Ingrese las URLs de las cámaras P2P para visualizarlas en el dashboard
              </p>
            </div>
            <CameraSettings cameras={cameras} onUpdateCamera={onUpdateCamera} />
          </TabsContent>

          <TabsContent value="sensors">
            <div className="mb-4">
              <h2 className="font-heading font-bold uppercase tracking-wider text-zinc-300">
                Configurar Sondas de Nivel
              </h2>
              <p className="text-sm text-zinc-500 mt-1">
                Ajuste los umbrales de alerta para cada sonda de nivel de agua
              </p>
            </div>
            <SensorSettings sensors={sensors} onUpdateSensor={onUpdateSensor} />
          </TabsContent>

          <TabsContent value="security">
            <div className="mb-4">
              <h2 className="font-heading font-bold uppercase tracking-wider text-zinc-300 flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-emerald-400" />
                Seguridad y Acceso
              </h2>
              <p className="text-sm text-zinc-500 mt-1">
                Gestiona el acceso biométrico por huella dactilar en este dispositivo
              </p>
            </div>
            <SecuritySettings />
          </TabsContent>
        </Tabs>

        {/* Info Card */}
        <Card className="mt-8 bg-blue-950/30 border-blue-900/50">
          <CardHeader>
            <CardTitle className="text-blue-400 text-sm font-heading uppercase tracking-wider flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" />
              Integración Modbus TCP
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-blue-200/70">
            <p>
              Los sensores están configurados para lectura automática vía Modbus TCP cada 5 minutos.
            </p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li><strong>SIBERIO:</strong> 82.223.48.250:5021 (Unit ID: 247)</li>
              <li><strong>CAIDERO:</strong> 82.223.48.250:5022 (Unit ID: 247)</li>
              <li><strong>PARRALILLO:</strong> 82.223.48.250:5023 (Unit ID: 1)</li>
            </ul>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default Settings;
